import ReactGA from 'react-ga4';

export interface EventArgs {
  category: string; // (string, Required): The category to which the event belongs. This is used to group events together in reports. Examples: 'Videos', 'Forms', 'Buttons'.
  action: string; // (string, Required): Describes the type of event or interaction being tracked. Examples: 'click', 'submit', 'play'. This is a primary way to categorize events.
  label?: string; // (string, Optional): Provides additional information about the event, often used to differentiate between similar events. Examples: 'Homepage Video', 'Signup Form', 'Submit Button'.
  value?: number; // (number, Optional): A numeric value associated with the event. Can represent quantities, sums, or other numerical data related to the event.
}
let isInitialized = false;

// Configuration Constants
const LOCALHOST_IDENTIFIER = 'localhost';
const SANDBOX_URL_IDENTIFIER = 'app-sandbox.flexe.com';
const PRODUCTION_URL_IDENTIFIER = 'app.flexe.com';

const TRACKING_ID_LOCALHOST = 'G-ENLMCWDLN8';
const TRACKING_ID_SANDBOX = 'G-DJQC341DE6';
const TRACKING_ID_PRODUCTION = 'G-NKYYPS3Q4K';

// Sensitive Data Regex Constants
const ADDRESS_REGEX = /\d{1,5}\s+[a-zA-Z\s]+\s+(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Court|Ct|Drive|Dr|Place|Pl|Terrace|Terr)\.?/i;
// Credit Card: Adjusted to be more specific about lengths and use word boundaries
// Visa: 13 or 16 digits, starting with 4
// Mastercard: 16 digits, starting with 51-55
// AMEX: 15 digits, starting with 34 or 37
// Diners Club: 14 digits, starting with 300-305, 36, or 38
// Discover: 16 digits, starting with 6011 or 65
const CREDIT_CARD_REGEX = /\b(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|6(?:011|5[0-9]{2})[0-9]{12})\b/i;
const SSN_REGEX = /\b\d{3}[- ]?\d{2}[- ]?\d{4}\b/i;

let pendingEvents: EventArgs[] = []; // Queue for events before initialization

interface AnalyticsConfig {
  trackingId: string;
  initializeOptions?: {
    gtagOptions?: Record<string, any>;
    gaOptions?: Record<string, any>;
  };
}

const getAnalyticsConfig = (url: string): AnalyticsConfig | null => {
  if (url.includes(LOCALHOST_IDENTIFIER)) {
    return {
      trackingId: TRACKING_ID_LOCALHOST,
      initializeOptions: {
        gtagOptions: {debug_mode: true},
        gaOptions: {debug_mode: true}
      }
    };
  }
  if (url.includes(SANDBOX_URL_IDENTIFIER)) {
    return {trackingId: TRACKING_ID_SANDBOX};
  }
  if (url.includes(PRODUCTION_URL_IDENTIFIER)) {
    return {trackingId: TRACKING_ID_PRODUCTION};
  }
  return null;
};

/**
 * Initializes react-ga4 with the hardcoded tracking ID.
 * Only initializes if it hasn't been initialized before.
 */
export const initializeAnalytics = () => {
  if (isInitialized) {
    return;
  }

  const config = getAnalyticsConfig(window.location.href);

  if (!config) {
    return;
  }

  ReactGA.initialize(config.trackingId, config.initializeOptions);
  isInitialized = true;

  // Process any pending events
  pendingEvents.forEach((event) => ReactGA.event(event));
  pendingEvents = []; // Clear the queue
};
/**
 * Sends an event to Google Analytics.
 * If react-ga4 is not initialized, it will attempt to initialize it.
 * @param {EventArgs} event - The event data to send.
 * @param {string} event.category - The category of the event (e.g., 'Videos', 'Forms'). Required.
 * @param {string} event.action - The action of the event (e.g., 'click', 'submit'). Required.
 * @param {string} [event.label] - Optional. Additional details about the event (e.g., 'Homepage Video').
 * @param {number} [event.value] - Optional. A numeric value associated with the event.
 *
 * @example
 * // Example: Tracking a user selecting a filter option from a dropdown
 * sendEvent({
 *   category: 'Locations Index Filter', // Grouping related interactions
 *   action: 'dropdown', // The type of interaction
 *   label: 'Location Category', // Specific detail about the selection
 * });
 */
export const sendEvent = (event: EventArgs) => {
  if (isInitialized) {
    handleSendEvent(event);
  } else {
    pendingEvents.push(event);
    initializeAnalytics();
  }
};

const containsSensitiveData = (eventData: EventArgs): boolean => {
  /**
   * Checks if the event data contains sensitive information using regex.
   *
   * @param eventData A string or dictionary containing the event data.
   * @returns True if sensitive data is found, False otherwise.
   */

  // Combine all regex patterns into a single list
  const patterns: RegExp[] = [ADDRESS_REGEX, CREDIT_CARD_REGEX, SSN_REGEX];

  // Safely collect string representations of relevant eventData properties.
  const dataToCheck: string[] = Object.values(eventData)
    .filter((value): value is string | number => typeof value === 'string' || typeof value === 'number')
    .map((value) => value.toString());

  for (const data of dataToCheck) {
    for (const pattern of patterns) {
      if (pattern.test(data)) {
        return true; // Sensitive data found
      }
    }
  }

  return false; // No sensitive data found
};

const handleSendEvent = (event: EventArgs) => {
  if (containsSensitiveData(event)) {
    return; // Skip the event
  }
  ReactGA.event(event);
};
